import { useState } from 'react';
import { Project } from '@bulthaup/api/db';
import { Button, Group, NumberInput, Stack, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { isEqual, pick } from 'lodash';

import { trpc } from '../../../../../services/trpc';
import { getDeliveryWeek } from '../documents/-components/-get-delivery-week';

const FIELDS_TO_COMPARE = [
  'availabilityWeek',
  'availabilityYear',
  'estimatedDeliveryDate',
  'deliveryDate',
  'measurementDate',
] as const;

export function DeliverySection({ project }: { project: Project }) {
  const utils = trpc.useUtils();
  const [localValues, setLocalValues] = useState(
    pick(project, FIELDS_TO_COMPARE),
  );
  const [hasChanges, setHasChanges] = useState(false);

  const update = trpc.projects.updateDelivery.useMutation({
    onSuccess: () => {
      notifications.show({
        title: 'Succès',
        message: 'Dates de livraison mises à jour',
        color: 'green',
      });
      utils.projects.get.invalidate({ slug: project.slug });
      setHasChanges(false);
    },
    onError: error => {
      notifications.show({
        title: 'Erreur',
        message: error.message,
        color: 'red',
      });
    },
  });

  const { deliveryWeek, deliveryYear, differenceInWeeks } = getDeliveryWeek(
    localValues.availabilityWeek,
    localValues.availabilityYear,
    localValues.estimatedDeliveryDate,
  );

  const handleChange = (field: string, value: number | Date | null) => {
    const newValues = {
      ...localValues,
      [field]: value,
    };

    if (!deliveryWeek || !deliveryYear) {
      newValues.deliveryDate = null;
    }

    setLocalValues(newValues);
    setHasChanges(
      !isEqual(
        pick(newValues, FIELDS_TO_COMPARE),
        pick(project, FIELDS_TO_COMPARE),
      ),
    );
  };

  const handleSave = async () => {
    await update.mutateAsync({
      id: project.id,
      availabilityWeek: localValues.availabilityWeek ?? null,
      availabilityYear: localValues.availabilityYear ?? null,
      estimatedDeliveryDate: localValues.estimatedDeliveryDate ?? null,
      deliveryDate: localValues.deliveryDate ?? null,
      measurementDate: localValues.measurementDate ?? null,
      installationDate: null,
    });
  };

  return (
    <Stack>
      <Group>
        <Text style={{ flex: 1 }}>Date de prise de cote :</Text>
        <DateInput
          withWeekNumbers
          placeholder="Sélectionner une date"
          valueFormat="DD/MM/YYYY"
          clearable
          value={localValues.measurementDate || null}
          onChange={date => {
            handleChange('measurementDate', date ? new Date(date) : null);
          }}
          style={{ width: 200 }}
        />
      </Group>
      <Group align="flex-end">
        <Text>Mise à dispo :</Text>
        <NumberInput
          placeholder="Semaine"
          min={1}
          max={53}
          value={localValues.availabilityWeek ?? ''}
          onChange={value => {
            handleChange('availabilityWeek', value ? Number(value) : null);
          }}
          style={{ flex: 1 }}
        />
        <Text>ème semaine</Text>
        <NumberInput
          placeholder="Année"
          min={0}
          max={new Date().getFullYear() + 10}
          value={localValues.availabilityYear ?? ''}
          onChange={value => {
            handleChange('availabilityYear', value ? Number(value) : null);
          }}
          style={{ flex: 1 }}
        />
      </Group>
      <Group align="center">
        <Text>Date de livraison estimée</Text>
        <DateInput
          withWeekNumbers
          placeholder="Sélectionner une date"
          valueFormat="DD/MM/YYYY"
          clearable
          value={localValues.estimatedDeliveryDate || null}
          onChange={date =>
            handleChange('estimatedDeliveryDate', date ? new Date(date) : null)
          }
          style={{ width: 200 }}
        />
      </Group>
      {deliveryWeek && deliveryYear && (
        <>
          <Group justify="space-between">
            <Text>Semaine de livraison: </Text>
            <Text>
              {deliveryWeek} ème {deliveryYear}
            </Text>
          </Group>
          <Group justify="flex-end">
            {differenceInWeeks >= 0 && (
              <Text
                ta="right"
                size="sm"
                c={differenceInWeeks > 3 ? 'red' : undefined}
              >
                {differenceInWeeks} semaine{differenceInWeeks > 1 ? 's' : ''}{' '}
                après la date de mise à disposition
              </Text>
            )}
            {differenceInWeeks < 0 && (
              <Text ta="right" size="sm" c="red">
                Date de livraison avant la date de mise à disposition
              </Text>
            )}
          </Group>

          <Group>
            <Text fw="bold" style={{ flex: 1 }}>
              Date de livraison :
            </Text>
            <DateInput
              withWeekNumbers
              placeholder="Sélectionner une date"
              valueFormat="DD/MM/YYYY"
              clearable
              value={localValues.deliveryDate || null}
              onChange={date =>
                handleChange('deliveryDate', date ? new Date(date) : null)
              }
              style={{ width: 200 }}
            />
          </Group>
        </>
      )}
      {hasChanges && (
        <Group justify="flex-end">
          <Button onClick={handleSave} loading={update.isPending}>
            Sauvegarder
          </Button>
        </Group>
      )}
    </Stack>
  );
}
